import { Color } from "@repo/app-types/constants/colors";
import { Size } from "@repo/ui/helpers/helpers";
import { getPillColor, Pill, type PillColor } from "@repo/ui/Pills/pill";
import type { FC } from "react";

/**
 * Single source of truth for the "very negative → very positive" scale
 * shared by every Topics-page metric expressed as a
 * difference from a scope-wide baseline weighted by call volume
 * (customer-experience score today, win impact next). Owns the band
 * type, the classifier, the signed-number formatter, and the pill that
 * renders a band — so a metric only needs to supply its own cutoffs.
 */

/**
 * Five-band signed scale — from `very_negative` through `neutral`
 * to `very_positive`. "Positive" means the topic sits above the
 * scope-wide baseline; "negative" means below.
 */
export type SignedBand =
  | "very_negative"
  | "negative"
  | "neutral"
  | "positive"
  | "very_positive";

/**
 * Absolute cutoffs that split a signed value into the five bands. A
 * `|value|` below `neutral` reads as no meaningful difference from the
 * baseline; at or above `strong` it reads as a "very" positive /
 * negative one. Each metric supplies its own pair, calibrated to its
 * value distribution.
 */
export interface SignedBandCutoffs {
  neutral: number;
  strong: number;
}

/**
 * Cutoffs separating the five impact bands, shared by every Topics-page
 * metric expressed as a volume-weighted signed delta from a scope-wide
 * baseline — the per-call gap (CX-score points, win-rate percentage
 * points) times the topic's share of calls. Placeholders to recalibrate
 * against the empirical distribution once enough topics exist.
 */
export const IMPACT_BAND_CUTOFFS: SignedBandCutoffs = {
  neutral: 0.2,
  strong: 1,
};

/**
 * Bucket a signed value into a `SignedBand` against the given cutoffs.
 * Inside the `±neutral` dead zone the value reads as `neutral`; outside
 * it, the sign picks positive/negative and a `|value|` at or past
 * `strong` promotes it to the `very_` variant.
 */
export function classifySignedBand(
  value: number,
  cutoffs: SignedBandCutoffs,
): SignedBand {
  const absValue = Math.abs(value);
  if (absValue < cutoffs.neutral) {
    return "neutral";
  }
  const polarity: "positive" | "negative" = value > 0 ? "positive" : "negative";
  return absValue >= cutoffs.strong ? `very_${polarity}` : polarity;
}

/**
 * Signed, fixed-decimal label. A nonzero value that would round to zero
 * is nudged out to ±1 in the least-significant place so its sign
 * survives the rounding. Only an exact zero renders as "+0".
 */
export function formatSignedValue(value: number, decimals = 2): string {
  const scale = 10 ** decimals;
  const rounded = Math.round(value * scale) / scale;
  const epsilon = 1 / scale;
  const display =
    rounded === 0 && value !== 0 ? (value < 0 ? -epsilon : epsilon) : rounded;
  const magnitude = Math.abs(display).toFixed(decimals);
  return display < 0 ? `-${magnitude}` : `+${magnitude}`;
}

/**
 * Text color for a signed figure rendered alongside its band — kept
 * consistent with the band's polarity.
 */
export function signedValueTextColorClass(band: SignedBand): string {
  if (band === "neutral") {
    return "text-muted-foreground";
  }
  return band.endsWith("positive") ? "text-emerald-700" : "text-red-700";
}

const BAND_LABELS: Record<SignedBand, string> = {
  very_negative: "Very negative",
  negative: "Negative",
  neutral: "Neutral",
  positive: "Positive",
  very_positive: "Very positive",
};

/**
 * Pill color per band. Polarity drives the hue — emerald above the
 * baseline, red below, gray for a flat `neutral` — and the `very_`
 * variants take a one-step deeper fill so big shifts read louder than
 * borderline ones at a glance.
 */
const BAND_COLORS: Record<SignedBand, PillColor> = {
  very_negative: Color.DarkRed,
  negative: Color.Red,
  neutral: Color.Gray,
  positive: Color.Emerald,
  very_positive: Color.DarkEmerald,
};

/** Labeled, color-coded pill for a single `SignedBand`. */
export const SignedBandPill: FC<{ band: SignedBand }> = ({ band }) => (
  <Pill
    size={Size.SMALL}
    color={getPillColor(BAND_COLORS[band])}
    label={BAND_LABELS[band]}
  />
);
